<template>
  <div>
    <breadcrumbs :home="vessel.name + ': Denature spirits'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row dense>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh()"/>
            </v-col>
            <v-col class="text-caption ml-5 mt-3 grey--text">{{vesselContents}}</v-col>
          </v-row>

          <v-row dense>
            <v-col  cols="6" sm="3" lg="2">
              <alcohol-input label="Volume"
                              :spirit="spirit || !this.vessel.contents.volume"
                              :tare="vessel.tare" v-model="transaction.volume" :litres.sync="transaction.litres"
                              :disabled="!edit" :hint="vesselVolume"/>
            </v-col>
          </v-row>

          <v-row dense >
            <v-col cols="12" sm="6" lg="4">
              <v-select label="To produce" v-model="transaction.excise" dense outlined
                        :items="[{value:'SpiritsReductionDenaturedSpiritsFuelDA2F',text:'Denatured alcohol - Fuel grades DA-2F'},
                                 {value:'SpiritsReductionDenaturedSpiritsFuelDA2G',text:'Denatured alcohol - Fuel grades DA-2G'},
                                 {value:'SpiritsReductionDenaturedSpiritsOther',text:'Denatured alcohol - Other grades'},
                                { value:'SpiritsReductionSpeciallyDenaturedSpirits',text:'Specially denatured alcohol'}]"
                        :hint="spirit || !this.vessel.contents.volume?'':'Only spirits can be denatured'" persistent-hint/>
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <v-autocomplete dense outlined label="Resulting item" v-model="transaction.itemId" :rules="[fieldRules.required]" :items="items"
                              item-value="id" item-text="name" no-data-text="No items found"
                              hint="Quantity of item will be in LAA" persistent-hint>
                <template v-slot:prepend-item>
                  <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="itemCreate=true">
                    <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new item
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense counter="255" :rules="[fieldRules.maxLength(255)]"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('production.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <inventory-items-quick-create v-model="itemCreate" @create="itemCreated"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InventoryItemsQuickCreate from '@/views/inventory/items/InventoryItemsQuickCreate'

export default {
  name: 'ExciseVesselDenature',
  mixins: [confirmLeave],
  components: {InventoryItemsQuickCreate},
	props: {
    vesselId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      vessel: {name:'',contents:{}},
      items: [],
      valid:true,
      saving:false,
      accounts: [],
      transaction: {vesselId:this.vesselId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), excise:'SpiritsReductionDenaturedSpiritsFuelDA2F',
                    volume: 0, litres: 0, itemId:'', comments: ''},
      dirty: false,
      itemCreate: false,
      wineCreate: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    spirit() {
      return this.vessel.contents?.alcoholType === 'Spirit';
    },
    vesselContents() {
      return this.vessel.contents.volume ? this.vessel.contents.alcoholName : '';
    },
    vesselVolume() {
      if (!this.transactionId)
        return this.vessel.contents.volume ? `${this.$number(this.vessel.contents.volume, 3)} ${this.spirit?'LAA':'litres'}` : '(empty)';
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
//    this.$lazyListLoad(this.items, 'items2', 'id,name', 100, !this.transactionId ? [{field:'active', comparator:'eq', value:'true'}] : []);
    this.items = (await this.$store.dispatch('items')).filter(item => item.active);
    const results = await this.$apiCall(`query ExciseVesselDenature($id:ID!,$edit:Boolean!) {
                                              exciseVesselDenature(id:$id) @include(if:$edit) {
                                                id,vesselId,date,volume,litres,excise,comments,itemId
                                              }
                                            }`,
                                        {id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.transactionId)
      this.transaction = results.data.exciseVesselDenature;
    if (!this.transactionId)
      this.edit = true;
    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    async refresh() {
      const results = await this.$apiCall(`query ExciseVesselDenature($id:ID!, $date:DateTime) {
                                                exciseVessel(id:$id) {
                                                  id,name,tare,
                                                  contents(date:$date) {alcoholType,alcoholName,volume}
                                                }
                                              }`,
                                          {id:this.transaction.vesselId || this.vesselId, date:this.transaction.date});
      if (!results.error) {
        this.vessel = results.data.exciseVessel;
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    async itemCreated(item) {
      this.items = (await this.$store.dispatch('items')).filter(item => item.active);
      this.transaction.itemId = item.id;
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.saving = true;
        const results = await this.$apiCall(`mutation ExciseVesselDenature($input: ExciseVesselDenatureInput!) {
                                                exciseVesselDenatureCreate(input:$input) {id}
                                              }`,
                                            {input:this.transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} denature spirits transaction for ${this.vessel.name}`});
        }
      }
		}
	}
}
</script>